import { Minus, Plus } from 'lucide-react';
import { cn } from './utils';

type Props = {
  value: number;
  onChange: (v: number) => void;
  /** Inclusive max value — defaults to 4 (the finals loser range). */
  max?: number;
  /** Inclusive min value — defaults to 0. */
  min?: number;
  disabled?: boolean;
  className?: string;
  /** Visual size — `sm` is ~h-8 (default), `md` is ~h-10. */
  size?: 'sm' | 'md';
  ariaLabel?: string;
};

/**
 * Compact +/- stepper for picking a small integer. Replaces a native
 * <input type="number"> when the range is tiny (finals loser score 0–4).
 * Native browser spinners look bad and a five-button row eats too much
 * horizontal space inside a card row, so we do the standard three-cell
 * stepper: `[−] N [+]`.
 */
export function ScoreSelector({
  value,
  onChange,
  max = 4,
  min = 0,
  disabled,
  className,
  size = 'sm',
  ariaLabel,
}: Props) {
  const clamp = (n: number) => Math.max(min, Math.min(max, n));
  const dec = () => !disabled && onChange(clamp(value - 1));
  const inc = () => !disabled && onChange(clamp(value + 1));

  const cellH = size === 'md' ? 'h-10' : 'h-8';
  const cellPad = size === 'md' ? 'px-2.5' : 'px-2';
  const numW = size === 'md' ? 'min-w-[36px] text-lg' : 'min-w-[28px] text-base';
  const iconSize = size === 'md' ? 'h-4 w-4' : 'h-3.5 w-3.5';

  const canDec = !disabled && value > min;
  const canInc = !disabled && value < max;

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex items-stretch rounded-md border border-amber-500/40 bg-card overflow-hidden',
        disabled && 'opacity-50',
        className,
      )}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          dec();
        }}
        disabled={!canDec}
        aria-label="Decrease score"
        className={cn(
          'flex items-center justify-center text-muted-foreground transition-colors',
          'hover:text-foreground hover:bg-muted',
          'disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed',
          cellH,
          cellPad,
        )}
      >
        <Minus className={iconSize} />
      </button>
      <div
        className={cn(
          'flex items-center justify-center font-bold tabular-nums text-amber-400',
          'border-x border-amber-500/30',
          cellH,
          numW,
        )}
      >
        {value}
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          inc();
        }}
        disabled={!canInc}
        aria-label="Increase score"
        className={cn(
          'flex items-center justify-center text-muted-foreground transition-colors',
          'hover:text-foreground hover:bg-muted',
          'disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed',
          cellH,
          cellPad,
        )}
      >
        <Plus className={iconSize} />
      </button>
    </div>
  );
}
